import React, { useEffect } from 'react';
import { Modal, Form, Button, Row, Col } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faSpinner } from '@fortawesome/free-solid-svg-icons';

import { useCreateSupportForm, useUpdateSupportForm } from '../../../../services/supportAdmin';
import { useForm } from '../../../../hooks/useForm';
import MondayBoardSelector from '../components/MondayBoardSelector';

const DEFAULTS = {
  formTitle: '',
  formSlug: '',
  formDescription: '',
  formIcon: 'MessageCircle',
  mondayBoardId: '',
  formOrder: 0,
  formAllowUploads: true,
  formActive: true,
};

const ICON_OPTIONS = [
  { value: 'MessageCircle', label: 'Message Circle' },
  { value: 'ClipboardList', label: 'Clipboard List' },
  { value: 'FileText', label: 'File Text' },
  { value: 'HelpCircle', label: 'Help Circle' },
  { value: 'Mail', label: 'Mail' },
  { value: 'Phone', label: 'Phone' },
  { value: 'Users', label: 'Users' },
  { value: 'Settings', label: 'Settings' },
  { value: 'AlertCircle', label: 'Alert Circle' },
  { value: 'CheckCircle', label: 'Check Circle' },
];

const SupportFormModal = ({ show, toggle, supportForm, onSuccess }) => {
  const { form, onChange, setValues } = useForm(DEFAULTS);

  useEffect(() => {
    if (supportForm && supportForm.formId) {
      setValues({
        ...DEFAULTS,
        ...supportForm,
        mondayBoardId: supportForm.mondayBoardId || '',
      });
    } else {
      setValues(DEFAULTS);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supportForm]);

  const createForm = useCreateSupportForm(
    () => {
      if (typeof onSuccess === 'function') {
        onSuccess();
      }
      toast.success('Support form created successfully.');
      toggle();
    },
    { onError: (err) => toast.error(err.message) },
  );

  const updateForm = useUpdateSupportForm(
    () => {
      if (typeof onSuccess === 'function') {
        onSuccess();
      }
      toast.success('Support form updated successfully.');
      toggle();
    },
    { onError: (err) => toast.error(err.message) },
  );

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-');
  };

  const handleTitleChange = (e) => {
    onChange(e);
    // Auto-generate slug if creating new form and slug is empty or matches auto-generated
    if (!supportForm?.formId) {
      const newSlug = generateSlug(e.target.value);
      const currentAutoSlug = generateSlug(form.formTitle);
      if (!form.formSlug || form.formSlug === currentAutoSlug) {
        setValues({ ...form, formTitle: e.target.value, formSlug: newSlug });
      }
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();

    const payload = {
      formTitle: form.formTitle,
      formSlug: form.formSlug,
      formDescription: form.formDescription,
      formIcon: form.formIcon,
      mondayBoardId: form.mondayBoardId || null,
      formOrder: parseInt(form.formOrder, 10) || 0,
      formAllowUploads: form.formAllowUploads,
      formActive: form.formActive,
    };

    if (supportForm && supportForm.formId) {
      updateForm.mutate({
        formId: supportForm.formId,
        payload,
      });
    } else {
      createForm.mutate(payload);
    }
  };

  const isEditing = supportForm && supportForm.formId;
  const isLoading = createForm.isLoading || updateForm.isLoading;

  return (
    <Modal show={show} onHide={() => toggle()} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{isEditing ? 'Edit Support Form' : 'Create Support Form'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <form id="support-form" onSubmit={onSubmit}>
          <Row>
            <Col md={8}>
              <Form.Group className="mb-3">
                <Form.Label>Form Title *</Form.Label>
                <Form.Control
                  name="formTitle"
                  onChange={handleTitleChange}
                  value={form.formTitle}
                  required
                  placeholder="e.g., DBQ Support"
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Display Order</Form.Label>
                <Form.Control
                  type="number"
                  name="formOrder"
                  onChange={onChange}
                  value={form.formOrder}
                  min="0"
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={8}>
              <Form.Group className="mb-3">
                <Form.Label>URL Slug *</Form.Label>
                <Form.Control
                  name="formSlug"
                  onChange={onChange}
                  value={form.formSlug}
                  required
                  placeholder="e.g., dbq-support"
                  pattern="^[a-z0-9-]+$"
                />
                <Form.Text className="text-muted">
                  Lowercase letters, numbers, and hyphens only. Used in URL: /support/{form.formSlug || 'slug'}
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Icon</Form.Label>
                <Form.Select
                  name="formIcon"
                  onChange={onChange}
                  value={form.formIcon}
                >
                  {ICON_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              name="formDescription"
              onChange={onChange}
              value={form.formDescription}
              rows={2}
              placeholder="Brief description shown on the support page tile"
            />
          </Form.Group>

          <hr />
          <h5>Monday.com Integration</h5>
          <p className="text-muted small">
            Connect this form to a Monday.com board to automatically create items when users submit.
            Column mappings are configured in Board Configs above.
          </p>

          <Form.Group className="mb-3">
            <Form.Label>Monday Board</Form.Label>
            <MondayBoardSelector
              value={form.mondayBoardId}
              onChange={(boardId) => setValues({ ...form, mondayBoardId: boardId })}
            />
            <Form.Text className="text-muted">
              Select a board that has been configured in Board Configs
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Check
              type="switch"
              id="form-allow-uploads"
              name="formAllowUploads"
              label="Allow file uploads (show attachments section on form)"
              checked={form.formAllowUploads}
              onChange={(e) => setValues({ ...form, formAllowUploads: e.target.checked })}
            />
          </Form.Group>

          {isEditing && (
            <Form.Group className="mb-3">
              <Form.Check
                type="switch"
                id="form-active"
                name="formActive"
                label="Form is active (visible on support page)"
                checked={form.formActive}
                onChange={(e) => setValues({ ...form, formActive: e.target.checked })}
              />
            </Form.Group>
          )}
        </form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="link" onClick={() => toggle()}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="success"
          form="support-form"
          disabled={isLoading}
        >
          <FontAwesomeIcon
            icon={isLoading ? faSpinner : faCheck}
            spin={isLoading}
          />{' '}
          {isEditing ? 'Update Form' : 'Create Form'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SupportFormModal;
